import {Controller} from "stimulus";
import {
    MARKER_FIELDS,
    markerGraphics,
    layerSummary,
    popupContent,
    clusterConfig,
    parcelOutFields,
    parcelPopupContent,
    locationGraphic,
    loadArcgis
} from "../helpers/record_map_graphics";

const ARCGIS_MODULES = ["esri/Map", "esri/views/MapView", "esri/layers/FeatureLayer", "esri/Graphic", "esri/widgets/Legend"];
const RECORDS_FAILED = "The records for this map could not be loaded. Reload the page to try again.";
const MAP_FAILED = "The map could not be loaded. Reload the page to try again.";
const LOCATION_FAILED = "Your location is not available. Check that your browser allows this site to use it.";
const LOCATION_OPTIONS = {enableHighAccuracy: true, timeout: 10000, maximumAge: 0};

// Draws /map: the customer's parcel layer plus one clustered layer per kind of
// record the current hat works with, from the markers feed (Maps::MarkersController).
// The feed is fetched while ArcGIS loads, and a summary of each layer -- how many
// records are pinned and how many had no coordinates -- is written below the map.
// Without a markers URL (the home page's map) it draws the parcels alone.
// When the page has a locate target and the browser can share its location, a
// "Center on my location" button moves the map to the user and pins where they are.
// Each connection gets a generation number; a callback from an earlier connection
// (the element was removed, or removed and put back, before its requests settled)
// finds the generation moved on and draws nothing.
export default class extends Controller {
    static targets = ["view", "status", "summary", "locate", "locationStatus"];

    connect() {
        this.generation = (this.generation || 0) + 1;
        const current = this.forCurrentConnection();
        this.announced = false;
        this.layersRequest = this.fetchLayers();
        this.layersRequest
            .then(current(layers => this.renderSummary(layers)))
            .catch(current(() => this.announce(RECORDS_FAILED)));

        loadArcgis(this.data.get("arcgisUrl"))
            .then(() => window.require(ARCGIS_MODULES, current((...modules) => this.drawMap(current, ...modules))))
            .catch(current(() => this.announce(MAP_FAILED)));
    }

    disconnect() {
        this.generation += 1;
        if (this.view) this.view.destroy();
        this.view = null;
    }

    // Wraps a callback so it runs only while the connection that made it is live.
    forCurrentConnection() {
        const generation = this.generation;
        return callback => (...args) => {
            if (this.generation === generation) callback(...args);
        };
    }

    fetchLayers() {
        if (!this.data.get("markersUrl")) return Promise.resolve([]);

        return fetch(this.data.get("markersUrl"), {headers: {Accept: "application/json"}, credentials: "same-origin"})
            .then(response => {
                if (!response.ok) throw new Error(`Markers feed returned ${response.status}`);
                return response.json();
            })
            .then(feed => feed.layers);
    }

    drawMap(current, Map, MapView, FeatureLayer, Graphic, Legend) {
        const map = new Map({basemap: "topo-vector"});
        this.view = new MapView({
            map: map,
            center: this.data.get("center").split(",").map(Number),
            zoom: Number(this.data.get("zoom")),
            container: this.viewTarget
        });
        const parcelLayer = this.parcelLayer(FeatureLayer);
        if (parcelLayer) map.add(parcelLayer, 0);
        this.Graphic = Graphic;
        this.offerLocation();

        this.layersRequest
            .then(current(layers => this.drawRecordLayers({map, layers, FeatureLayer, Graphic, Legend})))
            .catch(() => {});
    }

    drawRecordLayers({map, layers, FeatureLayer, Graphic, Legend}) {
        const drawn = layers.filter(layer => layer.markers.length > 0);
        drawn.forEach(layer => map.add(this.recordLayer({layer, FeatureLayer, Graphic})));
        if (drawn.length > 0) this.view.ui.add(new Legend({view: this.view}), "bottom-left");
    }

    recordLayer({layer, FeatureLayer, Graphic}) {
        return new FeatureLayer({
            title: layer.label,
            source: markerGraphics(layer).map(graphic => new Graphic(graphic)),
            fields: MARKER_FIELDS,
            objectIdField: "ObjectID",
            geometryType: "point",
            spatialReference: {wkid: 4326},
            renderer: {
                type: "simple",
                symbol: {type: "simple-marker", color: layer.color, size: 10, outline: {color: "white", width: 1.5}}
            },
            popupTemplate: {title: "{title}", content: popupContent(layer.link_text, layer.detail_label)},
            featureReduction: clusterConfig(layer.label)
        });
    }

    offerLocation() {
        if (this.hasLocateTarget && "geolocation" in navigator) this.locateTarget.hidden = false;
    }

    centerOnLocation() {
        const current = this.forCurrentConnection();
        navigator.geolocation.getCurrentPosition(
            current(position => this.showLocation(position.coords)),
            current(() => this.reportLocation(LOCATION_FAILED)),
            LOCATION_OPTIONS
        );
    }

    showLocation(coords) {
        if (!this.view) return;

        this.reportLocation(null);
        this.view.graphics.removeAll();
        this.view.graphics.add(new this.Graphic(locationGraphic(coords)));
        this.view.goTo({center: [coords.longitude, coords.latitude], zoom: Number(this.data.get("zoom"))});
    }

    reportLocation(message) {
        if (!this.hasLocationStatusTarget) return;

        this.locationStatusTarget.textContent = message || "";
        this.locationStatusTarget.hidden = !message;
    }

    // The customer's GIS parcels, as /map has always drawn them; absent when the
    // customer has no parcel server configured.
    parcelLayer(FeatureLayer) {
        const url = this.data.get("parcelUrl");
        if (!url) return null;

        const fields = JSON.parse(this.data.get("parcelFields"));
        return new FeatureLayer({
            title: "Parcels",
            url: url,
            outFields: parcelOutFields(fields),
            popupTemplate: {title: "Parcel Info", content: parcelPopupContent(fields, this.data.get("parcelLink"))}
        });
    }

    renderSummary(layers) {
        if (!this.hasSummaryTarget) return;

        // Hide the loading line, but not a failure the map has already announced.
        if (!this.announced) this.statusTarget.hidden = true;
        this.summaryTarget.replaceChildren(...layers.map(layer => this.summaryItem(layer)));
    }

    summaryItem(layer) {
        const item = document.createElement("li");
        item.className = "flex items-center gap-2 text-base/7 text-gray-700 sm:text-sm/6";

        const swatch = document.createElement("span");
        swatch.className = "size-3 shrink-0 rounded-full bg-[var(--swatch)]";
        swatch.style.setProperty("--swatch", layer.color);
        swatch.setAttribute("aria-hidden", "true");

        const text = document.createElement("span");
        const label = document.createElement("span");
        label.className = "font-medium text-gray-900";
        label.textContent = layer.label;
        const counts = document.createElement("span");
        counts.className = "tabular-nums";
        counts.textContent = layerSummary(layer);
        text.append(label, ": ", counts);

        item.append(swatch, text);
        return item;
    }

    announce(message) {
        if (!this.hasStatusTarget) return;

        this.announced = true;
        this.statusTarget.textContent = message;
        this.statusTarget.hidden = false;
    }
}
